// Claims carried by the current session's access token, not entries from the RBAC
// catalogue.
//
// These are built from `JwtPayloadCustom`, whose `permissions`, `roles` and `groups` claims
// are `string[]` — so a name is all the token has. The catalogue types with the same
// meaning, `Permission` / `Role` / `Group` in `@data/users/model/user`, come from the
// `/rbac` endpoints and carry `id` and `description` as well.
//
// They were once flagged as duplicates to merge (MODEL-3). They are not: nothing converts
// between them, and the token cannot supply the extra fields. Expressing these as
// `Pick<Role, 'name'>` would compile — the shapes are identical — but would assert a
// derivation that does not exist and tie the token's shape to the RBAC API contract.

// Why these carry a `claim` discriminant (RBAC-1).
//
// Without it all three are structurally `{ name: string }`, which makes them
// interchangeable with each other *and* assignable from the richer catalogue types. Before
// this discriminant existed, `hasPermissions(roles, [...])` compiled clean — a role list
// silently satisfying a permission check, across more than a hundred call sites. Nothing is
// known to have hit it; the point is that nothing could have caught it either.
//
// The discriminant is a real runtime property rather than a phantom type, so the types stay
// honest and the constructors below need no casts. Build claims with those constructors:
// an object literal will not satisfy these types by accident, which is the whole point.
export interface TokenPermission {
  readonly claim: 'permission'
  name: string
}

export interface TokenRole {
  readonly claim: 'role'
  name: string
}

export interface TokenGroup {
  readonly claim: 'group'
  name: string
}

export const tokenPermission = (name: string): TokenPermission => ({
  claim: 'permission',
  name,
})

export const tokenRole = (name: string): TokenRole => ({
  claim: 'role',
  name,
})

export const tokenGroup = (name: string): TokenGroup => ({
  claim: 'group',
  name,
})
